/**
 * Header search toggle.
 *
 * Collapsed, only the magnifying-glass button shows. Clicking it opens the
 * full-screen search overlay (CSS-driven off `.is-open`) and focuses the
 * input. Escape, the close button, or a click on the overlay backdrop closes
 * it again. On mobile the field renders inline in the menu and this state is
 * never opened.
 *
 * While open, the overlay is modal: Tab is trapped among its controls and
 * closing returns focus to the trigger button.
 *
 * Usage:
 *   <div class="search-toggle" x-data="searchToggle" :class="{ 'is-open': open }" @keydown.escape.window="close()" @keydown.tab.window="trapFocus( $event )">
 *     <button x-ref="button" @click="toggle()">…</button>
 *     <div class="search-toggle__overlay" x-ref="overlay" @click.self="close()">
 *       <input x-ref="input">
 *     </div>
 *   </div>
 */

document.addEventListener( 'alpine:init', () => {

	Alpine.data( 'searchToggle', () => ({

		open: false,

		toggle() {
			this.open ? this.close() : this.openSearch();
		},

		openSearch() {
			this.open = true;

			// Wait for the overlay to become visible before focusing the input.
			this.$nextTick( () => {
				this.$refs.input.focus();
			});
		},

		close() {
			if ( ! this.open ) {
				return;
			}

			this.open = false;

			// Return focus to the trigger so it doesn't strand on the hidden
			// overlay.
			this.$refs.button.focus();
		},

		// While the overlay is open it behaves as a modal: Tab cycles through
		// its three controls ( close, input, submit ) instead of escaping into
		// the page hidden behind it.
		trapFocus( event ) {
			if ( ! this.open ) {
				return;
			}

			const focusables = Array.from( this.$refs.overlay.querySelectorAll( 'button, input' ) );
			const first      = focusables[ 0 ];
			const last       = focusables[ focusables.length - 1 ];

			// Focus escaped ( e.g. a backdrop click landed it on <body> ) —
			// pull it back to the first control.
			if ( ! this.$refs.overlay.contains( document.activeElement ) ) {
				event.preventDefault();
				first.focus();
				return;
			}

			if ( event.shiftKey && document.activeElement === first ) {
				event.preventDefault();
				last.focus();
			} else if ( ! event.shiftKey && document.activeElement === last ) {
				event.preventDefault();
				first.focus();
			}
		},

		// Block the submit on an empty field and close the overlay instead, so
		// the magnifying glass never fires off a blank search.
		onSubmit( event ) {
			if ( '' === this.$refs.input.value.trim() ) {
				event.preventDefault();
				this.close();
			}
		},

	}) );
});
